import { Link } from "react-router-dom";
import {
  Phone,
  Wrench,
  Flame,
  ShieldCheck,
  Check,
  Star,
  Clock4,
  PoundSterling,
  MapPin,
  ArrowRight,
  Quote,
} from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import Seo from "@/components/Seo";
import { COMPANY } from "@/lib/site";

const HERO_IMG =
  "https://images.pexels.com/photos/7859953/pexels-photo-7859953.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";
const VAN_IMG =
  "https://images.pexels.com/photos/17976203/pexels-photo-17976203.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";

const SPECIALTIES = [
  {
    icon: Wrench,
    title: "Emergency Repairs",
    overline: "Rapid Response",
    body:
      "Boilers, leaks and burst pipes handled instantly by Gas Safe engineers dispatched across Hackney, Islington and Clapton.",
    points: ["Boiler breakdowns", "Burst pipes & leaks", "Radiator emergencies"],
  },
  {
    icon: Flame,
    title: "System Installations",
    overline: "Energy-Efficient Upgrades",
    body:
      "Premium boiler installations and full system upgrades supplied with up to 10-year manufacturer warranties.",
    points: ["Worcester & Vaillant", "Smart thermostat setup", "Power-flushing included"],
  },
  {
    icon: ShieldCheck,
    title: "Safety Inspections",
    overline: "Compliance & Certificates",
    body:
      "Official gas safety checks, CP12 landlord certificates and annual servicing — paperwork sorted on the day.",
    points: ["Annual boiler service", "CP12 certification", "Carbon monoxide testing"],
  },
];

const TESTIMONIALS = [
  {
    name: "Tulip Tuitt",
    when: "7 months ago",
    text:
      "Excellent service. Responsive, respectful and thorough. I wholeheartedly recommend him to you. He installed my old Cannon cooker, ensured that my setup met the current regulations and also that the cooker was in tip top condition. Thank you also for educating me about my boiler.",
  },
  {
    name: "Abass Dodoo",
    when: "3 months ago",
    text:
      "On behalf of One Drum Foundation, I would like to express our sincere gratitude for your exceptional support. You generously offered to complete the work free of charge, and after struggling with British Gas for such a long time, your ability to resolve the issue so quickly was truly remarkable. We deeply appreciate your dedication, professionalism, and willingness to help.",
  },
  {
    name: "chandra chandra",
    when: "2 months ago",
    text:
      "Ali been a real helpful technician. Punctual, clear with the root cause of the problem and does all this at a very reasonable price. Highly recommend this company. He also goes out of his way for stuff he doesn't deal with. Got some very good workmanship contacts who deals with other aspects of house which Ali can't deal. 100% trustworthy peoples.",
  },
  {
    name: "Jason Graham",
    when: "5 months ago",
    text:
      "He arrived on time, was professional and courteous, and did an excellent job repairing the boiler. The work was carried out efficiently and to a high standard, and hot water was restored without any issues. Everything was explained clearly and the job was completed promptly. Very happy with the service and would definitely recommend.",
  },
  {
    name: "Naomi V",
    when: "5 months ago",
    text:
      "Ali recently attended our new home to fix a leak under our sink that was dripping onto an awfully placed plug. He went above and beyond, moving the plug over to a safer location and even explaining our system to us (which isn't straight forward). He also helped sort out a hot water issue that we were having with our shower. Can't recommend Ali enough. Thank you!",
  },
];

export default function HomePage() {
  return (
    <>
      <Seo
        title="EL-Gas and Plumbing | Gas Safe Plumbers in Lower Clapton, Hackney & Islington"
        description="Gas Safe registered local plumbers in Lower Clapton, Hackney & Islington. Emergency boiler repairs, installations, landlord CP12 certificates. Rated 5/5 by 80+ customers."
        path="/"
      />
      {/* HERO */}
      <section
        className="relative isolate overflow-hidden bg-brand-navy text-white"
        data-testid="home-hero"
      >
        <div className="absolute inset-0 -z-10">
          <img
            src={HERO_IMG}
            alt="Engineer working on boiler"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-navy/95 via-brand-navy/85 to-brand-navy/60" />
          <div
            className="absolute inset-0 opacity-[0.06] mix-blend-overlay"
            style={{
              backgroundImage:
                "repeating-linear-gradient(45deg, #fff 0 2px, transparent 2px 12px)",
            }}
          />
        </div>

        <div className="container-x py-24 md:py-32 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8 animate-fade-up">
            <span className="inline-flex items-center gap-2 bg-brand-orange/15 border border-brand-orange/40 text-brand-orange px-3 py-1.5 rounded-sm text-xs font-bold tracking-[0.22em] uppercase">
              <Clock4 className="w-3.5 h-3.5" /> Same-day Local Dispatch · Hackney · Islington · E5
            </span>
            <h1 className="mt-6 font-heading font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[1.02]">
              Trusted Gas, Heating &amp;
              <span className="block text-brand-orange">Plumbing in Lower Clapton</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-200 max-w-2xl leading-relaxed">
              <span className="font-bold text-white">{COMPANY.reviewCount}+ local residents</span>{" "}
              have rated us <span className="font-bold text-white">5/5 stars</span>. Safe, certified
              and fast local dispatch when you need it most.
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link to="/booking" className="btn-primary" data-testid="hero-book-engineer-button">
                Book an Engineer Online <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/contact" className="btn-outline-white" data-testid="hero-get-quote-button">
                Get Instant Quote
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-6 text-sm">
              <a href={`tel:${COMPANY.phoneTel}`} className="inline-flex items-center gap-2 font-bold hover:text-brand-orange transition-colors" data-testid="hero-phone-link">
                <Phone className="w-4 h-4 text-brand-orange" /> {COMPANY.phone}
              </a>
              <span className="inline-flex items-center gap-2 text-slate-300">
                <MapPin className="w-4 h-4 text-brand-orange" /> {COMPANY.area}
              </span>
            </div>
          </div>

          <div className="lg:col-span-4 hidden lg:block">
            <div className="relative">
              <div className="absolute -top-3 -left-3 right-3 bottom-3 border-2 border-brand-orange rounded-sm" />
              <div className="relative bg-white text-slate-900 p-8 rounded-sm shadow-2xl">
                <p className="overline">Today’s availability</p>
                <p className="mt-2 font-heading font-black text-3xl tracking-tight">
                  Same-day slots open
                </p>
                <ul className="mt-5 space-y-3 text-sm">
                  {[
                    "Morning · 9:00 AM – 1:00 PM",
                    "Afternoon · 1:00 PM – 6:00 PM",
                    "Emergency callout · Call to check",
                  ].map((t) => (
                    <li key={t} className="flex items-center gap-3">
                      <span className="w-1.5 h-1.5 bg-brand-orange rounded-full" />
                      <span className="font-semibold">{t}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/booking" className="btn-primary w-full mt-6" data-testid="hero-card-book-button">
                  Reserve a slot
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF BANNER */}
      <section className="bg-white border-y border-slate-200" data-testid="social-proof-banner">
        <div className="container-x py-6 flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12">
          <div className="flex items-center gap-3">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-brand-orange text-brand-orange" />
              ))}
            </div>
            <p className="font-bold text-slate-900 text-base md:text-lg">
              Rated <span className="text-brand-orange">5/5</span> on Google Maps
            </p>
          </div>
          <span className="hidden md:block h-6 w-px bg-slate-300" />
          <p className="font-bold text-slate-700">
            {COMPANY.reviewCount}+ Independent Customer Reviews
          </p>
          <span className="hidden md:block h-6 w-px bg-slate-300" />
          <p className="font-bold text-slate-700 inline-flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-brand-navy" /> Gas Safe Registered
          </p>
        </div>
      </section>

      {/* SPECIALTIES */}
      <section className="py-24 bg-slate-50" data-testid="specialties-section">
        <div className="container-x">
          <div className="max-w-3xl">
            <span className="overline">Our Core Specialities</span>
            <h2 className="mt-3 font-heading font-black text-4xl md:text-5xl tracking-tighter text-brand-navy">
              The work we’re known for across East London.
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {SPECIALTIES.map((s) => (
              <article
                key={s.title}
                data-testid={`specialty-card-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="group relative bg-white border border-slate-200 p-8 rounded-sm shadow-sm hover:shadow-xl hover:border-brand-orange/60 transition-all"
              >
                <div className="grid place-items-center w-14 h-14 bg-brand-navy text-white rounded-sm">
                  <s.icon className="w-7 h-7 text-brand-orange" />
                </div>
                <p className="overline mt-6">{s.overline}</p>
                <h3 className="mt-2 font-heading font-black text-2xl tracking-tight text-brand-navy">
                  {s.title}
                </h3>
                <p className="mt-3 text-slate-600 leading-relaxed">{s.body}</p>
                <ul className="mt-5 space-y-2">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-center gap-2 text-sm text-slate-700">
                      <Check className="w-4 h-4 text-brand-orange" /> <span>{p}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/services"
                  className="mt-7 inline-flex items-center gap-2 text-brand-navy font-bold text-sm uppercase tracking-wide group-hover:text-brand-orange transition-colors"
                >
                  Learn more <ArrowRight className="w-4 h-4" />
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST GUARANTEE */}
      <section className="py-24 bg-brand-navy text-white relative overflow-hidden" data-testid="trust-section">
        <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="overline">The EL-Gas Guarantee</span>
            <h2 className="mt-3 font-heading font-black text-4xl md:text-5xl tracking-tighter">
              Established engineers. <span className="text-brand-orange">Honest pricing.</span> Local dispatch.
            </h2>
            <p className="mt-5 text-slate-300 leading-relaxed max-w-xl">
              We’re not a national call-centre. We’re a local Hackney &amp; Islington fleet with a registered
              office on Median Rd — the same engineers serve your street week in, week out.
            </p>

            <ul className="mt-9 space-y-5">
              {[
                {
                  icon: ShieldCheck,
                  title: "100% Gas Safe Registered",
                  body: "Every engineer fully certified, fully insured, ID-carded on arrival.",
                },
                {
                  icon: PoundSterling,
                  title: "Transparent Fixed Pricing",
                  body: "Quoted before work begins — no hidden fees, no callout surprises.",
                },
                {
                  icon: MapPin,
                  title: "Local Hackney, Islington & Clapton Dispatch",
                  body: "Vans live within minutes of E5 / E8 / E9 / N1 / N5 — fastest local response.",
                },
              ].map((b) => (
                <li key={b.title} className="flex items-start gap-4">
                  <span className="grid place-items-center w-12 h-12 bg-white/10 border border-white/15 rounded-sm shrink-0">
                    <b.icon className="w-6 h-6 text-brand-orange" />
                  </span>
                  <div>
                    <p className="font-heading font-bold text-xl">{b.title}</p>
                    <p className="text-slate-300 text-sm leading-relaxed mt-1">{b.body}</p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/booking" className="btn-primary" data-testid="trust-book-button">
                Book your engineer <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href={`tel:${COMPANY.phoneTel}`}
                className="btn-outline-white"
                data-testid="trust-call-button"
              >
                <Phone className="w-5 h-5" /> {COMPANY.phone}
              </a>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -top-4 -right-4 left-4 bottom-4 border-2 border-brand-orange rounded-sm" />
            <img
              src={VAN_IMG}
              alt="EL-Gas van on the road"
              className="relative w-full h-[480px] object-cover rounded-sm shadow-2xl"
            />
            <div className="absolute bottom-6 left-6 right-6 md:right-auto bg-white text-slate-900 p-5 rounded-sm shadow-xl border-l-4 border-brand-orange">
              <p className="overline">Live now</p>
              <p className="font-heading font-black text-xl tracking-tight mt-1">
                3 engineers within 15 minutes of E5
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 bg-white" data-testid="testimonials-section">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="max-w-2xl">
              <span className="overline">Reviews from real neighbours</span>
              <h2 className="mt-3 font-heading font-black text-4xl md:text-5xl tracking-tighter text-brand-navy">
                Why E5 keeps EL-Gas on speed-dial.
              </h2>
            </div>
            <div className="inline-flex items-center gap-3 bg-slate-50 border border-slate-200 px-5 py-3 rounded-sm">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-brand-orange text-brand-orange" />
                ))}
              </div>
              <p className="font-bold text-slate-900">
                5 / 5 from {COMPANY.reviewCount}+ reviews
              </p>
            </div>
          </div>

          <div className="mt-12">
            <Carousel
              opts={{ align: "start", loop: true }}
              className="w-full"
              data-testid="testimonials-carousel"
            >
              <CarouselContent className="-ml-4">
                {TESTIMONIALS.map((t) => (
                  <CarouselItem
                    key={t.name}
                    className="pl-4 md:basis-1/2 lg:basis-1/3"
                  >
                    <article
                      className="h-full bg-slate-50 border border-slate-200 p-7 rounded-sm shadow-sm hover:shadow-md transition-shadow flex flex-col"
                      data-testid={`testimonial-${t.name.split(" ")[0].toLowerCase()}`}
                    >
                      <div className="flex items-start justify-between">
                        <Quote className="w-7 h-7 text-brand-orange" />
                        <div className="flex">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className="w-4 h-4 fill-brand-orange text-brand-orange" />
                          ))}
                        </div>
                      </div>
                      <p className="mt-4 text-slate-800 leading-relaxed text-[15px] flex-1">
                        "{t.text}"
                      </p>
                      <div className="mt-6 pt-4 border-t border-slate-200 flex items-center justify-between">
                        <div>
                          <p className="font-bold text-brand-navy">{t.name}</p>
                          <p className="text-xs text-slate-500 uppercase tracking-wider">
                            {t.when}
                          </p>
                        </div>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                          Google Review
                        </span>
                      </div>
                    </article>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious
                data-testid="testimonials-prev"
                className="hidden md:flex -left-4 lg:-left-6 h-12 w-12 rounded-sm border-2 border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white"
              />
              <CarouselNext
                data-testid="testimonials-next"
                className="hidden md:flex -right-4 lg:-right-6 h-12 w-12 rounded-sm border-2 border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white"
              />
            </Carousel>
          </div>

          {/* Leave a Google Review CTA */}
          <div
            className="mt-14 bg-brand-navy text-white rounded-sm p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6 shadow-lg"
            data-testid="leave-review-cta"
          >
            <div className="flex items-start gap-5">
              <div className="grid place-items-center w-14 h-14 bg-brand-orange rounded-sm shrink-0">
                <Star className="w-7 h-7 fill-white text-white" />
              </div>
              <div>
                <p className="overline text-brand-orange">Loved the service?</p>
                <h3 className="mt-2 font-heading font-black text-2xl md:text-3xl tracking-tighter">
                  Help another neighbour find us.
                </h3>
                <p className="mt-2 text-slate-300 text-sm md:text-base max-w-xl">
                  A quick 30-second Google review goes a long way for a local
                  independent trade — thank you for supporting Ali &amp; the team.
                </p>
              </div>
            </div>
            <a
              href="https://g.page/r/CWAkmnivoEIPEBM/review"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary shrink-0"
              data-testid="leave-google-review-button"
            >
              <Star className="w-5 h-5 fill-white text-white" />
              Leave a Google Review
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="bg-brand-orange text-white py-14" data-testid="cta-strip">
        <div className="container-x flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <p className="overline text-white/80">Need help right now?</p>
            <h3 className="font-heading font-black text-3xl md:text-4xl tracking-tighter mt-2">
              Speak to a Gas Safe engineer in under 30 seconds.
            </h3>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <a
              href={`tel:${COMPANY.phoneTel}`}
              className="inline-flex items-center gap-2 bg-white text-brand-navy px-7 py-4 font-bold rounded-sm hover:bg-slate-100 transition-colors"
              data-testid="cta-strip-call-button"
            >
              <Phone className="w-5 h-5" /> {COMPANY.phone}
            </a>
            <Link to="/booking" className="btn-secondary" data-testid="cta-strip-book-button">
              Book online
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
